import {
  readScopedPresentationHint,
  scopedPresentationHintKey,
  writeScopedPresentationHint,
  type HintStorage,
} from '@/lib/scoped-presentation-hint';

/**
 * Cold-load layout hint: did this thread last restore with its artifact panel
 * docked? Thread skeletons read it to reserve the docked panel column before
 * the thread query resolves, so the chat does not render full width and then
 * shrink when the panel opens.
 *
 * Presentation only. It stores a boolean (never a path), is scoped to the
 * confirmed identity + workspace + thread route, and must never authorize
 * access, fetch artifacts, or make a placeholder interactive. A stale hint
 * costs one layout correction when the real state arrives.
 */
export type ThreadPanelLayoutHintScope = {
  identity: string | null;
  orgSlug: string | null | undefined;
  threadRouteRef: string | null | undefined;
};

const HINT_KEY_PREFIX = 'imai:thread-panel-docked:v1';

export function threadPanelLayoutHintKey(
  scope: ThreadPanelLayoutHintScope
): string | null {
  return scopedPresentationHintKey(HINT_KEY_PREFIX, [
    scope.identity,
    scope.orgSlug,
    scope.threadRouteRef,
  ]);
}

/**
 * How the identity's most recently loaded thread in this workspace restored.
 * A thread with no hint of its own (never loaded on this device) follows it:
 * people who keep the panel docked keep it docked everywhere, so reserving
 * the column is the better guess than painting the chat full width.
 */
export function threadPanelLastDockedKey(
  scope: Omit<ThreadPanelLayoutHintScope, 'threadRouteRef'>
): string | null {
  return scopedPresentationHintKey(`${HINT_KEY_PREFIX}:last`, [
    scope.identity,
    scope.orgSlug,
  ]);
}

export function readThreadPanelLayoutHint(
  storage: HintStorage | null | undefined,
  scope: ThreadPanelLayoutHintScope
): boolean {
  const own = readScopedPresentationHint(
    storage,
    threadPanelLayoutHintKey(scope)
  );
  if (own !== null) return own === '1';
  return (
    readScopedPresentationHint(storage, threadPanelLastDockedKey(scope)) === '1'
  );
}

/** Stores the thread's flag ('1' docked, '0' not) and the workspace's last. */
export function writeThreadPanelLayoutHint(
  storage: HintStorage | null | undefined,
  scope: ThreadPanelLayoutHintScope,
  docked: boolean
): void {
  if (!threadPanelLayoutHintKey(scope)) return;
  const flag = docked ? '1' : '0';
  writeScopedPresentationHint(storage, threadPanelLayoutHintKey(scope), flag);
  writeScopedPresentationHint(storage, threadPanelLastDockedKey(scope), flag);
}

type PointerEnvironment = {
  /** `'ontouchstart' in window`: callers pass fields, never window itself. */
  hasTouchStart: boolean;
  maxTouchPoints: number;
  matchMedia: (query: string) => { matches: boolean };
};

/** Touch-primary devices open the panel as a drawer, never docked. */
export function isTouchPrimaryEnvironment(env: PointerEnvironment): boolean {
  const hasTouch = env.hasTouchStart || env.maxTouchPoints > 0;
  return hasTouch && env.matchMedia('(pointer: coarse)').matches;
}
